import { useCallback, useEffect, useRef, useState } from 'react';

export interface PreviewPlayback {
  currentTime: number;
  isPlaying: boolean;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  seek: (time: number) => void;
  step: (deltaSeconds: number) => void;
}

/** Drives a simple wall-clock based playback position, looping is left to the caller (stops at duration). */
export function usePreviewPlayback(totalDurationSeconds: number): PreviewPlayback {
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number>(0); // performance.now() offset such that time = now - startRef

  const stopRaf = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const tick = useCallback(() => {
    const now = performance.now();
    const time = (now - startRef.current) / 1000;
    if (time >= totalDurationSeconds) {
      setCurrentTime(totalDurationSeconds);
      setIsPlaying(false);
      stopRaf();
      return;
    }
    setCurrentTime(time);
    rafRef.current = requestAnimationFrame(tick);
  }, [stopRaf, totalDurationSeconds]);

  const play = useCallback(() => {
    if (isPlaying) return;
    setCurrentTime((current) => {
      const startingTime = current >= totalDurationSeconds ? 0 : current;
      startRef.current = performance.now() - startingTime * 1000;
      return startingTime;
    });
    setIsPlaying(true);
  }, [isPlaying, totalDurationSeconds]);

  const pause = useCallback(() => {
    setIsPlaying(false);
    stopRaf();
  }, [stopRaf]);

  const togglePlay = useCallback(() => (isPlaying ? pause() : play()), [isPlaying, play, pause]);

  const seek = useCallback(
    (time: number) => {
      const clamped = Math.min(totalDurationSeconds, Math.max(0, time));
      setCurrentTime(clamped);
      startRef.current = performance.now() - clamped * 1000;
    },
    [totalDurationSeconds]
  );

  const step = useCallback((delta: number) => seek(currentTime + delta), [seek, currentTime]);

  useEffect(() => {
    if (isPlaying) {
      rafRef.current = requestAnimationFrame(tick);
    }
    return stopRaf;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  return { currentTime, isPlaying, play, pause, togglePlay, seek, step };
}
